import type { AdminOperationsResource } from '@meowmoir/shared';

export const OPERATIONS: Record<
  AdminOperationsResource,
  {
    label: string;
    description: string;
    timeLabel: string;
    placeholder: string;
    statuses: Array<[string, string]>;
  }
> = {
  orders: {
    label: '订阅订单',
    description: '查询历史支付订单、金额和失败原因。订单保留已注销账号的历史记录。',
    timeLabel: '下单时间',
    placeholder: '订单号、用户昵称或 ID',
    statuses: [
      ['pending', '待支付'],
      ['paid', '已支付'],
      ['failed', '失败'],
      ['closed', '已关闭'],
      ['refunded', '已退款'],
    ],
  },
  subscriptions: {
    label: '订阅权益',
    description: '按实际有效期判断订阅状态。免费模式下，历史订阅记录不影响免费功能使用。',
    timeLabel: '创建时间',
    placeholder: '用户昵称或用户 ID',
    statuses: [
      ['active', '有效'],
      ['expired', '已到期'],
      ['inactive', '未生效'],
    ],
  },
  jobs: {
    label: 'AI 生成任务',
    description: '追踪护理分析与体检解读的队列、处理阶段、结果编号及失败原因。',
    timeLabel: '创建时间',
    placeholder: '用户、猫咪名称或数字 ID',
    statuses: [
      ['queued', '排队中'],
      ['processing', '处理中'],
      ['succeeded', '已完成'],
      ['failed', '失败'],
    ],
  },
  extractions: {
    label: '体检提取',
    description: '查看上传批次的提取结果、使用模型和错误说明，直达猫咪体检报告。',
    timeLabel: '上传时间',
    placeholder: '用户、猫咪名称或数字 ID',
    statuses: [
      ['processing', '处理中'],
      ['succeeded', '已完成'],
      ['failed', '失败'],
    ],
  },
  reminders: {
    label: '提醒执行',
    description: '启用且未归档规则的执行记录。逾期表示用户尚未完成，不代表通知投递失败。',
    timeLabel: '计划时间',
    placeholder: '提醒标题、用户或猫咪',
    statuses: [
      ['pending', '待完成（含逾期）'],
      ['overdue', '已逾期'],
      ['completed', '已完成'],
      ['skipped', '已跳过'],
      ['cancelled', '已取消'],
    ],
  },
  members: {
    label: '共同照护',
    description: '查看猫咪的照护成员、角色、加入时间及邀请人。',
    timeLabel: '加入时间',
    placeholder: '用户、猫咪名称或数字 ID',
    statuses: [
      ['owner', '主人'],
      ['caregiver', '共同照护'],
    ],
  },
  quotas: {
    label: 'AI 额度流水',
    description: '查看额度消耗和返还；详情中的周期用量是当前值，不是事件发生时的余额。',
    timeLabel: '发生时间',
    placeholder: '用户昵称或用户 ID',
    statuses: [
      ['consumed', '已消耗'],
      ['returned', '已返还'],
    ],
  },
  conflicts: {
    label: '账号冲突',
    description: '通过支持编号定位登录身份冲突，并核对两个账号的资料。',
    timeLabel: '发生时间',
    placeholder: '支持编号、用户昵称或 ID',
    statuses: [
      ['open', '待处理'],
      ['resolved', '已解决'],
      ['dismissed', '已忽略'],
    ],
  },
};

export function operationResource(value: string | null): AdminOperationsResource {
  return value && Object.hasOwn(OPERATIONS, value) ? (value as AdminOperationsResource) : 'orders';
}

export function operationsQuery(params: URLSearchParams, resource: AdminOperationsResource) {
  const positive = (value: string | null, fallback: number, max: number) =>
    value && /^\d+$/.test(value) && Number(value) > 0 && Number(value) <= max
      ? Number(value)
      : fallback;
  const page = positive(params.get('page'), 1, 100000);
  const pageSize = positive(params.get('pageSize'), 20, 100);
  const q = (params.get('q') || '').trim().slice(0, 100);
  const requestedStatus = params.get('status');
  const status = OPERATIONS[resource].statuses.some(([key]) => key === requestedStatus)
    ? requestedStatus!
    : 'all';
  const days = ['today', '7', '30', '90'].includes(params.get('days') || '')
    ? params.get('days')!
    : 'all';
  const query = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (q) query.set('q', q);
  if (status !== 'all') query.set('status', status);
  if (days !== 'all') query.set('days', days);
  return { page, pageSize, q, status, days, url: `/admin/operations/${resource}?${query}` };
}
